import React, { useEffect, useState } from "react";
import {
    Button,
    makeStyles,
    tokens,
} from "@fluentui/react-components";
import { Pivot, PivotItem } from "@fluentui/react";
import {
    CheckmarkCircleFilled,
    DismissCircleRegular,
} from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { APPS_BY_CATEGORY, AZURE_APPS } from "../../Config/ConsentConfig";
import { getAdminConsentUrl, registerConsentApi, checkConsentStatusApi } from "../../Services/HeedifyServices";
import { useNotification } from "../GenericComponent/NotificationSystem";
import { getAccessToken } from "../../Services/GraphServices";
import { useAbility } from "../../Contexts/AbilityContext";
import InfoButton from "../GenericComponent/InfoButton";
import "../CSS/GlobalSettingsComponent.css";

const useStyles = makeStyles({
    successIcon: {
        color: tokens.colorPaletteGreenForeground1,
        fontSize: "24px",
        marginRight: "8px",
    },
    disabledIcon: {
        color: tokens.colorNeutralForegroundDisabled,
        fontSize: "24px",
        marginRight: "8px",
    },
});

const GlobalSettingsComponent = (props) => {
    const { t } = useTranslation();
    const { showError } = useNotification();
    const styles = useStyles();
    const { accounts, instance } = useMsal();
    const activeAccount = accounts?.[0];
    const tenantId = props.ACTIVE_TENANT;
    const userId = activeAccount?.localAccountId;
    const ability = useAbility();
    
    // Check if we're processing a consent callback
    const [isProcessingCallback, setIsProcessingCallback] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("admin_consent") === "True";
    });
    
    // State for active tab - restore from sessionStorage if available
    const [selectedTab, setSelectedTab] = useState(() => {
        const savedTab = sessionStorage.getItem("globalSettingsActiveTab");
        if (savedTab) {
            sessionStorage.removeItem("globalSettingsActiveTab");
            return savedTab;
        }
        return "commun";
    });
    
    // Utiliser le scope actif ou "default" si non défini
    const scope = props.store_activeScope || "default";

    // Permissions pour GlobalSettings avec le scope
    const permissions = {
        main: {
            canRead: ability?.can("read", `GlobalSettings.main@${scope}`),
            canCreate: ability?.can("create", `GlobalSettings.main@${scope}`),
            canUpdate: ability?.can("update", `GlobalSettings.main@${scope}`),
            canDelete: ability?.can("delete", `GlobalSettings.main@${scope}`)
        }
    };

    // Onboarding : tenant neuf sans permission. On laisse alors voir la page et CONSENTIR
    // (les permissions normales arriveront après le 1er consent + bootstrap backend).
    const bootstrapMode = props.bootstrapMode;
    const canConsent = permissions.main.canUpdate || bootstrapMode;

    // Charger le statut des consents au montage du composant
    useEffect(() => {
        if (!tenantId || !userId || !instance || !activeAccount) return;
        
        getAccessToken(instance, activeAccount)
            .then((token) => {
                return checkConsentStatusApi(tenantId, userId, token);
            })
            .then((result) => {
                if (result?.consentedApp) {
                    props.setConsentedApps(result.consentedApp);
                }
            })
            .catch((error) => {
                console.error("Error loading consent status:", error);
            });
    }, [tenantId, userId, instance, activeAccount]);

    // Check for consent callback in URL
    useEffect(() => {
        if (!tenantId || !userId || !instance || !activeAccount) return;

        const params = new URLSearchParams(window.location.search);
        const adminConsent = params.get("admin_consent");
        const tenant = params.get("tenant");
        const currentPath = window.location.pathname;
        
        if (adminConsent === "True" && tenant) {
            // Find which app was consented based on the redirect path
            const consentedApp = Object.values(AZURE_APPS).find(
                (app) => app.redirectPath === currentPath
            );

            if (consentedApp) {
                // Find which category this app belongs to
                let appCategory = "commun";
                for (const [category, apps] of Object.entries(APPS_BY_CATEGORY)) {
                    if (apps.some(app => app.clientId === consentedApp.clientId)) {
                        appCategory = category.toLowerCase();
                        break;
                    }
                }

                // Register the consent and wait before redirecting
                const registerConsent = async () => {
                    try {
                        // SECONDE BARRIÈRE. L'URL vise maintenant explicitement le tenant affiché
                        // (getAdminConsentUrl), donc `?tenant=` devrait toujours correspondre. On
                        // vérifie quand même : c'est Microsoft qui décide du tenant final, pas nous,
                        // et le paramètre était jusqu'ici lu puis IGNORÉ — on enregistrait sur
                        // ACTIVE_TENANT quoi qu'il arrive. Une base qui affirme « consenti » pour un
                        // client qui ne l'est pas est pire qu'une erreur : elle ne se voit pas.
                        if (tenant !== tenantId) {
                            console.error(
                                `Consentement accordé sur le tenant ${tenant} alors que le portail affiche ${tenantId} : non enregistré.`
                            );
                            showError(t("CONSENT_WRONG_TENANT"));
                            return;
                        }
                        const token = await getAccessToken(instance, activeAccount);
                        await registerConsentApi(tenantId, userId, consentedApp.clientId, token);
                        const updatedConsents = [...(props.consentedApps || []), consentedApp.clientId];
                        props.setConsentedApps(updatedConsents);
                        
                        // Save the tab to return to
                        sessionStorage.setItem("globalSettingsActiveTab", appCategory);
                    } catch (error) {
                        console.error("Error registering consent:", error);
                    } finally {
                        // Redirect without showing the UI
                        window.location.href = "/globalSettings";
                    }
                };

                registerConsent();
            } else {
                window.location.href = "/globalSettings";
            }
        } else {
            // Not a callback, show the UI
            setIsProcessingCallback(false);
        }
    }, [tenantId, userId, instance, activeAccount]);

    // Check if an app is consented - use clientId not id!
    const isAppConsented = (clientId) => {
        const isConsented = props.consentedApps && props.consentedApps.indexOf(clientId) >= 0;
        return isConsented;
    };
    // Render consent status icon
    const ConsentStatusIcon = ({ clientId }) => {
        return isAppConsented(clientId) ? (
            <CheckmarkCircleFilled className={styles.successIcon} />
        ) : (
            <DismissCircleRegular className={styles.disabledIcon} />
        );
    };

    // Get full redirect URI for consent callback
    const getRedirectUri = (redirectPath) => {
        return `${window.location.origin}${redirectPath}`;
    };

    // Si pas de permission de lecture, ne rien afficher — SAUF en onboarding (tenant neuf),
    // où l'on doit pouvoir atteindre la page pour faire le 1er consent.
    if (!permissions.main.canRead && !bootstrapMode) {
        return (
            <div className="access-denied-message">
                <h2>{t("GLOBAL_SETTINGS.ACCESS_DENIED")}</h2>
                <p>{t("GLOBAL_SETTINGS.NO_PERMISSION_TO_VIEW")}</p>
            </div>
        );
    }

    // Si on traite un callback, ne rien afficher (on va rediriger)
    if (isProcessingCallback) {
        return (
            <div className="global-settings-container">
                <div style={{ padding: "20px", textAlign: "center" }}>
                    <p>{t("GLOBAL_SETTINGS.PROCESSING_CONSENT") || "Processing consent..."}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="global-settings-container">
            <div className="global-settings-header">
                <h1 className="global-settings-title">{t("GLOBAL_SETTINGS.CONSENT_TITLE")}</h1>
                <InfoButton infoKey="CONSENT_TITLE" category="GLOBAL_SETTINGS" />
            </div>

            <Pivot 
                className="global-settings-pivot"
                selectedKey={selectedTab}
                onLinkClick={(item) => setSelectedTab(item.props.itemKey)}
            >
                <PivotItem headerText={t("GLOBAL_SETTINGS.COMMUN")} itemKey="commun">
                    <div className="apps-container">
                        {APPS_BY_CATEGORY.COMMUN.map((app) => (
                            <div key={app.id} className="app-item">
                                <div className="app-info">
                                    <div className="app-name-section">
                                        <span className="app-name">{t(`GLOBAL_SETTINGS.${app.name}`)}</span>
                                        <InfoButton infoKey={app.name} category="GLOBAL_SETTINGS" />
                                    </div>
                                </div>
                                <div className="app-actions">
                                    <ConsentStatusIcon clientId={app.clientId} />
                                    <Button
                                        appearance="primary"
                                        as="a"
                                        href={getAdminConsentUrl(
                                            app.clientId,
                                            getRedirectUri(app.redirectPath),
                                            tenantId
                                        )}
                                        rel="noopener noreferrer"
                                        disabled={!canConsent}
                                    >
                                        {t("GLOBAL_SETTINGS.CONSENT_PERMISSIONS")}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </PivotItem>

                <PivotItem headerText={t("GLOBAL_SETTINGS.EXTEND")} itemKey="extend">
                    <div className="apps-container">
                        {APPS_BY_CATEGORY.EXTEND.map((app) => (
                            <div key={app.id} className="app-item">
                                <div className="app-info">
                                    <div className="app-name-section">
                                        <span className="app-name">{t(`GLOBAL_SETTINGS.${app.name}`)}</span>
                                        <InfoButton infoKey={app.name} category="GLOBAL_SETTINGS" />
                                    </div>
                                </div>
                                <div className="app-actions">
                                    <ConsentStatusIcon clientId={app.clientId} />
                                    <Button
                                        appearance="primary"
                                        as="a"
                                        href={getAdminConsentUrl(
                                            app.clientId,
                                            getRedirectUri(app.redirectPath),
                                            tenantId
                                        )}
                                        rel="noopener noreferrer"
                                        disabled={!canConsent}
                                    >
                                        {t("GLOBAL_SETTINGS.CONSENT_PERMISSIONS")}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </PivotItem>

                <PivotItem headerText={t("GLOBAL_SETTINGS.TPE")} itemKey="tpe">
                    <div className="apps-container">
                        {APPS_BY_CATEGORY.TPE.map((app) => (
                            <div key={app.id} className="app-item">
                                <div className="app-info">
                                    <div className="app-name-section">
                                        <span className="app-name">{t(`GLOBAL_SETTINGS.${app.name}`)}</span>
                                        <InfoButton infoKey={app.name} category="GLOBAL_SETTINGS" />
                                    </div>
                                </div>
                                <div className="app-actions">
                                    <ConsentStatusIcon clientId={app.clientId} />
                                    <Button
                                        appearance="primary"
                                        as="a"
                                        href={getAdminConsentUrl(
                                            app.clientId,
                                            getRedirectUri(app.redirectPath),
                                            tenantId
                                        )}
                                        rel="noopener noreferrer"
                                        disabled={!canConsent}
                                    >
                                        {t("GLOBAL_SETTINGS.CONSENT_PERMISSIONS")}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </PivotItem>
            </Pivot>
        </div>
    );
};

export default GlobalSettingsComponent;
